// Карта доступности модулей в памяти приложения.
//
// Zustand, а не TanStack Query как источник истины, по одной причине: карту
// читают из мест, где хука запроса быть не может и где она нужна СИНХРОННО —
// прежде всего при первом рендере навигации из офлайн-снапшота (§6.4). Запрос
// (`modules.availability`) остаётся в Query и только ПИТАЕТ этот стор
// (`providers/module-availability-provider.tsx`).
//
// Персиста Zustand здесь нет намеренно: снапшот имеет собственный формат с
// версией и собственный срок жизни (`snapshot.ts`), и заворачивать его во
// второй механизм хранения — это две записи одного факта.

import type {
	ModuleAvailabilityEntry,
	ModuleAvailabilityOutput,
} from "@studyhub/shared/contracts/modules";
import type { ModuleId } from "@studyhub/shared/module";
import { create } from "zustand";
import { type AvailabilitySource, indexAvailability } from "./resolve";
import { readModuleAvailabilitySnapshot } from "./snapshot";

interface ModuleAvailabilityState {
	source: AvailabilitySource;
	byId: ReadonlyMap<ModuleId, ModuleAvailabilityEntry>;
	/**
	 * Чья это карта. Сравнение по id пользователя, а не по признаку «кто-то
	 * залогинен»: в одной вкладке меняют аккаунт, и карта прошлого пользователя
	 * с другим тарифом — это чужие открытые и чужие закрытые разделы
	 * (инцидент `40b62b3b`).
	 */
	ownerUserId: string | null;
	/** Поколение карты. Растёт при каждом изменении раскатки на сервере. */
	version: number;
	/** Сколько секунд карта считается свежей (приходит с сервера). */
	ttlSeconds: number | null;
	/**
	 * Показывать ли коммерцию. `null` = сервер ещё не отвечал (первый запуск,
	 * офлайн без снапшота, карта чужого пользователя).
	 *
	 * Именно `null`, а не `false`: «не знаем» и «запрещено» — разные состояния,
	 * и подменять первое вторым нельзя даже когда исход совпадает. Решение о
	 * показе принимает читатель, зная платформу.
	 */
	commerceVisible: boolean | null;
	/** Принять ответ сервера. Ответ «из прошлой жизни» отбрасывается. */
	applyServer: (payload: ModuleAvailabilityOutput, expectedUserId: string) => void;
	/** Забыть карту (выход, смена аккаунта). */
	reset: () => void;
}

/**
 * Стартовое состояние читается из localStorage СИНХРОННО, на импорте модуля,
 * то есть до первого рендера навигации. Это и есть весь смысл снапшота: меню
 * рисуется сразу правильным, а не полным-и-через-кадр-урезанным.
 *
 * Владелец снапшота здесь ещё не проверен — пользователь на этот момент может
 * быть неизвестен. Сверку делает `useModuleAvailability`: пока `ownerUserId` не
 * совпал с текущим пользователем, карта наружу не отдаётся.
 */
function initialState(): Pick<
	ModuleAvailabilityState,
	"source" | "byId" | "ownerUserId" | "version" | "ttlSeconds" | "commerceVisible"
> {
	const snapshot = readModuleAvailabilitySnapshot();
	if (!snapshot) {
		return {
			source: "unknown",
			byId: new Map(),
			ownerUserId: null,
			version: 0,
			ttlSeconds: null,
			commerceVisible: null,
		};
	}
	return {
		source: "snapshot",
		byId: indexAvailability(snapshot.payload.modules),
		ownerUserId: snapshot.payload.userId,
		version: snapshot.payload.version,
		ttlSeconds: snapshot.payload.ttlSeconds,
		commerceVisible: snapshot.payload.commerceVisible,
	};
}

export const useModuleAvailabilityStore = create<ModuleAvailabilityState>((set, get) => ({
	...initialState(),
	applyServer: (payload, expectedUserId) => {
		// Ответ, отправленный до смены аккаунта, приходит уже в другой мир.
		// Записать его — значит показать новому пользователю разделы прошлого.
		if (payload.userId !== expectedUserId) return;
		// Гонка «рефетч обогнал сокет-событие»: более старое поколение карты не
		// должно откатывать уже применённое. Версия только растёт.
		const current = get();
		if (current.ownerUserId === payload.userId && payload.version < current.version) return;
		set({
			source: "server",
			byId: indexAvailability(payload.modules),
			ownerUserId: payload.userId,
			version: payload.version,
			ttlSeconds: payload.ttlSeconds,
			commerceVisible: payload.commerceVisible,
		});
	},
	reset: () =>
		set({
			source: "unknown",
			byId: new Map(),
			ownerUserId: null,
			version: 0,
			ttlSeconds: null,
			commerceVisible: null,
		}),
}));
